// frontend/src/pages/discover/adventures/api.ts
//
// Transport-Schicht der Abenteuer-Vorlagen (Paket R2-12). Bewusst eine EIGENE
// Datei neben `src/api/discover.ts`: die Nachbarpakete R2-10 (Filterleiste) und
// R2-11 (Katalog-Lokalisierung) fassen jene Datei gleichzeitig an.
//
// Server-Vertrag (server/adventure-templates.js):
//
//   GET  /api/discover/adventures?setting&usk&lang&q&cursor&limit
//        → 200 { items: AdventureTemplateItem[], next_cursor: string|null }
//   GET  /api/discover/adventures/:key
//        → 200 AdventureTemplateItem      403 usk_blocked   404 not_found
//   POST /api/discover/adventures/:key/play
//        → 201 { campaign_id, cost }      403 usk_blocked   404 not_found
//                                          402 insufficient_coins
//
// Die USK-Stufe erzwingt der SERVER (`min(requested, Kontostufe)`); `usk` darf
// nur verschaerfen. Der Client filtert nicht nach.
//
// Wie bei „Entdecken" laufen diese Daten NUR ueber react-query — nie in Dexie:
// es sind Katalogdaten, kein Nutzerbestand.

import { ApiError, apiRequest } from '../../../api/client';

/** Die sieben Welten — identisch zu DISCOVER_SETTINGS in src/api/discover.ts. */
export const ADVENTURE_SETTINGS = [
  'alltag',
  'fantasy',
  'scifi',
  'postapokalypse',
  'cyberpunk',
  'historisch',
  'krimi',
] as const;
export type AdventureSetting = (typeof ADVENTURE_SETTINGS)[number];

/** Obergrenzen der Filterleiste („bis …"). age_0 ist kein sinnvoller Filter. */
export const ADVENTURE_USK_LEVELS = ['age_6', 'age_12', 'age_16', 'age_18'] as const;

export interface AdventureTemplateItem {
  /** Stabiler Vorlagen-Schluessel (kein numerisches Id). */
  key: string;
  title: string;
  teaser: string;
  setting: string;
  usk: string;
  spicy: boolean;
  cover_url: string | null;
  sort_order: number;
}

export interface AdventureListResponse {
  items: AdventureTemplateItem[];
  next_cursor: string | null;
}

export interface AdventurePlayResponse {
  /** Ziel der Navigation: /chat/:campaign_id */
  campaign_id: number;
  /** Preis in Coins (aktuell 0). */
  cost: number;
  /** true = derselbe Start kam binnen Sekunden doppelt; es ist die bestehende Kampagne. */
  reused?: boolean;
}

export interface AdventureListFilter {
  setting?: string | null;
  /** Darf die serverseitige Stufe nur VERSCHAERFEN. */
  usk?: string | null;
  /** Textsprache; filtert nicht (jede Vorlage gibt es in jeder Sprache). */
  lang?: string | null;
  q?: string | null;
  limit?: number;
}

function appendParam(params: URLSearchParams, key: string, value: unknown): void {
  if (value === null || value === undefined) return;
  const text = String(value).trim();
  if (!text) return;
  params.set(key, text);
}

/** Baut den Query-String — exportiert, damit Tests ihn pruefen koennen. */
export function buildAdventureQuery(filter: AdventureListFilter, cursor?: string | null): string {
  const params = new URLSearchParams();
  appendParam(params, 'setting', filter.setting);
  appendParam(params, 'usk', filter.usk);
  appendParam(params, 'lang', filter.lang);
  appendParam(params, 'q', filter.q);
  appendParam(params, 'limit', filter.limit);
  appendParam(params, 'cursor', cursor);
  const text = params.toString();
  return text ? `?${text}` : '';
}

/** Defensive Normalisierung: fehlende Felder duerfen die UI nicht brechen. */
export function normalizeAdventureItem(raw: unknown): AdventureTemplateItem | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const row = raw as Record<string, unknown>;
  const key = typeof row.key === 'string' ? row.key : '';
  if (!key) return null;
  return {
    key,
    title: typeof row.title === 'string' ? row.title : '',
    teaser: typeof row.teaser === 'string' ? row.teaser : '',
    setting: typeof row.setting === 'string' ? row.setting : '',
    usk: typeof row.usk === 'string' ? row.usk : '',
    spicy: row.spicy === true,
    cover_url: typeof row.cover_url === 'string' && row.cover_url ? row.cover_url : null,
    sort_order: Number(row.sort_order) || 0,
  };
}

export async function listDiscoverAdventures(
  filter: AdventureListFilter,
  cursor?: string | null,
): Promise<AdventureListResponse> {
  const response = await apiRequest<Partial<AdventureListResponse>>(
    `/api/discover/adventures${buildAdventureQuery(filter, cursor)}`,
  );
  const items = (Array.isArray(response?.items) ? response.items : [])
    .map(normalizeAdventureItem)
    .filter((row): row is AdventureTemplateItem => row !== null);
  const next = response?.next_cursor;
  return { items, next_cursor: typeof next === 'string' && next ? next : null };
}

export async function getDiscoverAdventure(key: string): Promise<AdventureTemplateItem> {
  const raw = await apiRequest<unknown>(`/api/discover/adventures/${encodeURIComponent(key)}`);
  const item = normalizeAdventureItem(raw);
  if (!item) throw new Error('adventure_malformed');
  return item;
}

export async function playDiscoverAdventure(key: string): Promise<AdventurePlayResponse> {
  // Unter Last brach der erste Starter-Aufruf nach dem allgemeinen 25s-Limit
  // ab, obwohl der Server den Start noch abarbeitete (C56). Ein eigener,
  // begrenzter Zeitrahmen vermeidet den zweiten Tipp; kein automatischer
  // POST-Retry, weil ein verlorenes Ergebnis bereits eine Kampagne sein kann.
  const result = await apiRequest<AdventurePlayResponse>(
    `/api/discover/adventures/${encodeURIComponent(key)}/play`,
    { method: 'POST', timeoutMs: 60_000 },
  );
  const id = Number(result?.campaign_id);
  if (!Number.isSafeInteger(id) || id <= 0) throw new Error('adventure_start_missing_id');
  return { ...result, campaign_id: id };
}

/** Server-Fehlercode → i18n-Key fuer den Toast. */
export function adventureErrorKey(error: unknown, fallbackKey: string): string {
  if (error instanceof ApiError) {
    const code = typeof error.data === 'object' && error.data !== null
      ? (error.data as { error?: unknown }).error
      : undefined;
    if (error.status === 403 || code === 'usk_blocked') return 'discover.adventure_play_usk_blocked';
    if (error.status === 402 || code === 'insufficient_coins') return 'coins.insufficient';
    if (error.status === 404) return 'discover.adventure_not_found_body';
  }
  return fallbackKey;
}

/** react-query-Schluessel — lokal, damit src/api/queryKeys.ts unberuehrt bleibt. */
export const adventureQueryKeys = {
  all: ['discover', 'adventures'] as const,
  list: (filter: Record<string, unknown>) => ['discover', 'adventures', 'list', filter] as const,
  detail: (key: string) => ['discover', 'adventures', 'detail', key] as const,
};
